<template>
  <PageShell
    title="Reports"
    subtitle="Profit & Loss • revenue, COGS, selling + operating expenses • net profit"
  >
    <template #actions>
      <button class="ll-pill ghost" type="button" @click="resetFilters">Reset</button>
      <button class="ll-pill" type="button" @click="fetchReport" :disabled="loading">
        {{ loading ? "Generating…" : "Generate" }}
      </button>
    </template>

    <div class="toast" v-if="toast">{{ toast }}</div>

    <!-- Filters -->
    <section class="card">
      <div class="card-top">
        <div>
          <div class="card-title">Filters</div>
          <div class="micro subtle">
            Pick a date range and generate the report. Dates are converted to UTC boundaries.
          </div>
        </div>

        <div class="micro dim">
          Range:
          <b>{{ startDate && endDate ? `${startDate} → ${endDate}` : rangeKey.toUpperCase() }}</b>
        </div>
      </div>

      <div class="filters">
        <label class="field">
          <span class="k">Start</span>
          <input type="date" v-model="startDate" class="input" />
        </label>

        <label class="field">
          <span class="k">End</span>
          <input type="date" v-model="endDate" class="input" />
        </label>

        <div class="btns">
          <button class="ll-pill ghost" type="button" @click="applyRange('7d')" title="Last 7 days">
            7D
          </button>
          <button class="ll-pill ghost" type="button" @click="applyRange('30d')" title="Last 30 days">
            30D
          </button>
          <button class="ll-pill ghost" type="button" @click="applyRange('90d')" title="Last 90 days">
            90D
          </button>
          <button class="ll-pill ghost" type="button" @click="applyRange('ytd')" title="Year to date">
            YTD
          </button>
          <button class="ll-pill ghost" type="button" @click="applyRange('all')" title="All time">
            ALL
          </button>
        </div>
      </div>

      <div class="snapshot">
        <div class="snap">
          <div class="k">Revenue</div>
          <div class="v mono">${{ fmtMoney(totals.revenue) }}</div>
        </div>
        <div class="snap">
          <div class="k">COGS</div>
          <div class="v mono">${{ fmtMoney(totals.cogs) }}</div>
        </div>
        <div class="snap">
          <div class="k">Gross</div>
          <div class="v mono" :class="totals.gross < 0 ? 'neg' : ''">
            ${{ fmtMoney(totals.gross) }}
          </div>
        </div>
        <div class="snap">
          <div class="k">Net</div>
          <div class="v mono" :class="totals.net < 0 ? 'neg' : ''">
            ${{ fmtMoney(totals.net) }}
          </div>
        </div>
      </div>
    </section>

    <!-- Empty -->
    <section class="card empty" v-if="!loading && !hasData">
      <div class="empty-title">No data yet</div>
      <div class="empty-sub">Pick a range and click Generate.</div>
    </section>

    <!-- Loading skeleton -->
    <section class="card" v-if="loading">
      <div class="skeleton-table">
        <div class="sk-row" v-for="n in 10" :key="n">
          <div class="sk-bar w55"></div>
          <div class="sk-bar w20"></div>
        </div>
      </div>
    </section>

    <!-- Report -->
    <section class="card" v-if="!loading && hasData">
      <div class="table-wrap">
        <table class="table">
          <thead>
            <tr>
              <th class="left">Report Line</th>
              <th class="right">Amount</th>
              <th class="right">%</th>
            </tr>
          </thead>

          <tbody>
            <!-- Revenue -->
            <tr class="section">
              <td class="left">
                <div class="row-title">
                  <span class="strong">Revenue</span>
                  <span class="pill neutral">Sales</span>
                </div>
              </td>
              <td class="right mono">${{ fmtMoney(totals.revenue) }}</td>
              <td class="right mono">100.00%</td>
            </tr>

            <!-- COGS -->
            <tr>
              <td class="left"><span class="strong">Cost of Goods Sold</span></td>
              <td class="right mono">${{ fmtMoney(totals.cogs) }}</td>
              <td class="right mono">{{ pctOfRevenue(totals.cogs) }}</td>
            </tr>

            <!-- Gross -->
            <tr class="total">
              <td class="left">
                <div class="row-title">
                  <span class="strong">Gross Profit</span>
                  <span class="pill" :class="heatGross.cls" :title="heatGross.title">
                    {{ heatGross.label }}
                  </span>
                </div>
              </td>
              <td class="right mono" :class="totals.gross < 0 ? 'neg' : ''">
                ${{ fmtMoney(totals.gross) }}
              </td>
              <td class="right mono">{{ grossPct }}</td>
            </tr>

            <!-- Selling -->
            <tr class="section">
              <td class="left">
                <div class="row-title">
                  <span class="strong">Selling Expenses</span>
                  <button class="mini ghost" type="button" @click="copyText('Selling Expenses')">
                    Copy
                  </button>
                </div>
              </td>
              <td class="right mono">${{ fmtMoney(totals.selling) }}</td>
              <td class="right mono">{{ pctOfRevenue(totals.selling) }}</td>
            </tr>
            <tr>
              <td class="left">— Freight Out</td>
              <td class="right mono">${{ fmtMoney(n(reportData.total_freight)) }}</td>
              <td class="right mono">{{ pctOfRevenue(n(reportData.total_freight)) }}</td>
            </tr>
            <tr>
              <td class="left">— Taxes (Sales)</td>
              <td class="right mono">${{ fmtMoney(n(reportData.total_taxes)) }}</td>
              <td class="right mono">{{ pctOfRevenue(n(reportData.total_taxes)) }}</td>
            </tr>
            <tr>
              <td class="left">— Fees</td>
              <td class="right mono">${{ fmtMoney(n(reportData.total_fees)) }}</td>
              <td class="right mono">{{ pctOfRevenue(n(reportData.total_fees)) }}</td>
            </tr>

            <!-- Inventory adjustments -->
            <tr class="section">
              <td class="left">
                <div class="row-title">
                  <span class="strong">Inventory Adjustments</span>
                  <span class="pill neutral" title="Applied to net profit">
                    {{ n(reportData.total_inventory_adjustments) >= 0 ? "↑ Add" : "↓ Subtract" }}
                  </span>
                </div>
              </td>
              <td class="right mono" :class="n(reportData.total_inventory_adjustments) < 0 ? 'neg' : ''">
                ${{ fmtMoney(n(reportData.total_inventory_adjustments)) }}
              </td>
              <td class="right mono">{{ pctOfRevenue(Math.abs(n(reportData.total_inventory_adjustments))) }}</td>
            </tr>

            <!-- Operating -->
            <tr class="section">
              <td class="left">
                <div class="row-title">
                  <span class="strong">Operating Expenses</span>
                  <span class="pill neutral">By Category</span>
                </div>
              </td>
              <td class="right mono">${{ fmtMoney(n(reportData.total_expenses)) }}</td>
              <td class="right mono">{{ pctOfRevenue(n(reportData.total_expenses)) }}</td>
            </tr>

            <tr v-if="sortedExpenseCats.length === 0">
              <td class="left dim"><em>No categorized expenses</em></td>
              <td class="right mono">$0.00</td>
              <td class="right mono">0.00%</td>
            </tr>

            <tr v-for="e in sortedExpenseCats" :key="e.category_name">
              <td class="left">— {{ e.category_name }}</td>
              <td class="right mono">${{ fmtMoney(n(e.total_cost)) }}</td>
              <td class="right mono">{{ pctOfRevenue(n(e.total_cost)) }}</td>
            </tr>

            <!-- Net -->
            <tr class="total net">
              <td class="left">
                <div class="row-title">
                  <span class="strong">Net Profit</span>
                  <span class="pill" :class="heatNet.cls" :title="heatNet.title">
                    {{ heatNet.label }}
                  </span>
                </div>
              </td>
              <td class="right mono" :class="totals.net < 0 ? 'neg' : ''">
                ${{ fmtMoney(totals.net) }}
              </td>
              <td class="right mono">{{ netPct }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="foot">
        <button class="ll-pill ghost" type="button" @click="copySummary">
          Copy Summary
        </button>
        <div class="micro dim">
          Tip: Use the copy buttons to paste figures into emails/notes quickly.
        </div>
      </div>
    </section>
  </PageShell>
</template>

<script>
import api from "@/services/api";
import PageShell from "@/components/layout/PageShell.vue";

export default {
  name: "ProfitLossReport",
  components: { PageShell },

  data() {
    return {
      loading: false,
      toast: "",
      rangeKey: "30d",

      startDate: "",
      endDate: "",

      reportData: {
        total_revenue: 0,
        total_cogs: 0,
        total_freight: 0,
        total_taxes: 0,
        total_fees: 0,
        total_selling_expenses: 0,
        total_expenses: 0,
        total_inventory_adjustments: 0,
        net_profit: 0,
        expense_categories: [],
      },
    };
  },

  computed: {
    hasData() {
      // treat "generated" as having a non-null object (even if zeros),
      // but require at least one number present to avoid showing empty on first load
      const r = this.reportData || {};
      const sum =
        this.n(r.total_revenue) +
        this.n(r.total_cogs) +
        this.n(r.total_expenses) +
        Math.abs(this.n(r.total_inventory_adjustments)) +
        this.n(r.total_freight) +
        this.n(r.total_taxes) +
        this.n(r.total_fees);
      return sum !== 0;
    },

    totals() {
      const revenue = this.n(this.reportData.total_revenue);
      const cogs = this.n(this.reportData.total_cogs);
      const freight = this.n(this.reportData.total_freight);
      const taxes = this.n(this.reportData.total_taxes);
      const fees = this.n(this.reportData.total_fees);
      const selling = freight + taxes + fees;
      const invAdj = this.n(this.reportData.total_inventory_adjustments);
      const opx = this.n(this.reportData.total_expenses);

      const gross = revenue - cogs;
      // Keep the same rule you had: net = rev - cogs - selling - opx + invAdj
      const net = revenue - cogs - selling - opx + invAdj;

      return { revenue, cogs, selling, gross, net };
    },

    grossPct() {
      const rev = this.totals.revenue;
      if (rev <= 0) return "N/A";
      return `${this.fmtPct(this.totals.gross / rev)}`;
    },

    netPct() {
      const rev = this.totals.revenue;
      if (rev <= 0) return "N/A";
      return `${this.fmtPct(this.totals.net / rev)}`;
    },

    heatGross() {
      const rev = this.totals.revenue;
      if (rev <= 0) return { cls: "neutral", label: "—", title: "No revenue" };
      const pct = this.totals.gross / rev;
      if (this.totals.gross < 0) return { cls: "loss", label: "💀 Loss", title: `Gross is negative (${this.fmtPct(pct)})` };
      if (pct >= 0.45) return { cls: "hot", label: "🔥 Hot", title: `Very strong gross margin (${this.fmtPct(pct)})` };
      if (pct >= 0.30) return { cls: "good", label: "👍 Good", title: `Strong gross margin (${this.fmtPct(pct)})` };
      if (pct >= 0.15) return { cls: "thin", label: "😬 Thin", title: `Thin gross margin (${this.fmtPct(pct)})` };
      return { cls: "meh", label: "🫤 Meh", title: `Low gross margin (${this.fmtPct(pct)})` };
    },

    heatNet() {
      const rev = this.totals.revenue;
      if (rev <= 0) return { cls: "neutral", label: "—", title: "No revenue" };
      const pct = this.totals.net / rev;
      if (this.totals.net < 0) return { cls: "loss", label: "💀 Loss", title: `Net is negative (${this.fmtPct(pct)})` };
      if (pct >= 0.25) return { cls: "hot", label: "🔥 Hot", title: `Very strong net margin (${this.fmtPct(pct)})` };
      if (pct >= 0.15) return { cls: "good", label: "👍 Good", title: `Strong net margin (${this.fmtPct(pct)})` };
      if (pct >= 0.06) return { cls: "thin", label: "😬 Thin", title: `Thin net margin (${this.fmtPct(pct)})` };
      return { cls: "meh", label: "🫤 Meh", title: `Low net margin (${this.fmtPct(pct)})` };
    },

    sortedExpenseCats() {
      const arr = Array.isArray(this.reportData.expense_categories)
        ? this.reportData.expense_categories
        : [];
      return [...arr].sort((a, b) => this.n(b?.total_cost) - this.n(a?.total_cost));
    },
  },

  mounted() {
    this.applyRange("30d");
  },

  methods: {
    n(v) {
      const x = Number(v);
      return Number.isFinite(x) ? x : 0;
    },

    toISODate(d) {
      return d.toISOString().slice(0, 10);
    },

    applyRange(key) {
      this.rangeKey = key;

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      if (key === "all") {
        this.startDate = "";
        this.endDate = "";
        return;
      }

      if (key === "ytd") {
        const start = new Date(today.getFullYear(), 0, 1);
        this.startDate = this.toISODate(start);
        this.endDate = this.toISODate(today);
        return;
      }

      const days = key === "7d" ? 7 : key === "30d" ? 30 : 90;
      const start = new Date(today);
      start.setDate(start.getDate() - (days - 1));

      this.startDate = this.toISODate(start);
      this.endDate = this.toISODate(today);
    },

    resetFilters() {
      this.applyRange("30d");
      this.reportData = {
        total_revenue: 0,
        total_cogs: 0,
        total_freight: 0,
        total_taxes: 0,
        total_fees: 0,
        total_selling_expenses: 0,
        total_expenses: 0,
        total_inventory_adjustments: 0,
        net_profit: 0,
        expense_categories: [],
      };
    },

    pctOfRevenue(amount) {
      const rev = this.totals.revenue;
      if (rev <= 0) return "N/A";
      return this.fmtPct(amount / rev);
    },

    fmtMoney(n) {
      const x = this.n(n);
      return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(x);
    },

    fmtPct(ratio) {
      const x = this.n(ratio) * 100;
      return `${x.toFixed(2)}%`;
    },

    // UTC boundary helper (keeps your behavior)
    toUtcBounds(startDate, endDate) {
      const s = startDate ? new Date(startDate).toISOString().split("T")[0] + "T00:00:00.000Z" : "";
      const e = endDate ? new Date(endDate).toISOString().split("T")[0] + "T23:59:59.999Z" : "";
      return { s, e };
    },

    async fetchReport() {
      try {
        this.loading = true;

        const { s, e } = this.toUtcBounds(this.startDate, this.endDate);

        const res = await api.get("/reports/profit-loss", {
          params: { start_date: s, end_date: e },
        });

        const data = res?.data || {};
        this.reportData = {
          total_revenue: this.n(data.total_revenue),
          total_cogs: this.n(data.total_cogs),
          total_freight: this.n(data.total_freight),
          total_taxes: this.n(data.total_taxes),
          total_fees: this.n(data.total_fees),
          total_selling_expenses: this.n(data.total_selling_expenses),
          total_expenses: this.n(data.total_expenses),
          total_inventory_adjustments: this.n(data.total_inventory_adjustments),
          net_profit: this.n(data.net_profit), // backend may send; we still compute net for display
          expense_categories: Array.isArray(data.expense_categories) ? data.expense_categories : [],
        };

        // Keep your exact net rule (override backend if needed)
        const revenue = this.n(this.reportData.total_revenue);
        const cogs = this.n(this.reportData.total_cogs);
        const selling =
          this.n(this.reportData.total_freight) +
          this.n(this.reportData.total_taxes) +
          this.n(this.reportData.total_fees);
        const opx = this.n(this.reportData.total_expenses);
        const invAdj = this.n(this.reportData.total_inventory_adjustments);

        this.reportData.net_profit = revenue - cogs - selling - opx + invAdj;
      } catch (err) {
        console.error("Error fetching P&L report:", err);
        this.toast = "Could not generate report";
        setTimeout(() => (this.toast = ""), 1200);
      } finally {
        this.loading = false;
      }
    },

    async copyText(text) {
      const val = String(text || "");
      if (!val) return;

      try {
        await navigator.clipboard.writeText(val);
        this.toast = "Copied";
        setTimeout(() => (this.toast = ""), 900);
      } catch {
        const el = document.createElement("textarea");
        el.value = val;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
        this.toast = "Copied";
        setTimeout(() => (this.toast = ""), 900);
      }
    },

    copySummary() {
      const lines = [
        `P&L (${this.startDate || "ALL"} → ${this.endDate || "ALL"})`,
        `Revenue: $${this.fmtMoney(this.totals.revenue)}`,
        `COGS: $${this.fmtMoney(this.totals.cogs)}`,
        `Gross: $${this.fmtMoney(this.totals.gross)} (${this.grossPct})`,
        `Selling: $${this.fmtMoney(this.totals.selling)} (${this.pctOfRevenue(this.totals.selling)})`,
        `Operating: $${this.fmtMoney(this.n(this.reportData.total_expenses))} (${this.pctOfRevenue(this.n(this.reportData.total_expenses))})`,
        `Inventory Adj: $${this.fmtMoney(this.n(this.reportData.total_inventory_adjustments))}`,
        `Net: $${this.fmtMoney(this.totals.net)} (${this.netPct})`,
      ];
      this.copyText(lines.join("\n"));
    },
  },
};
</script>

<style scoped>
/* Shared platform vibe (matches your new report page) */
.card {
  border-radius: 16px;
  border: 1px solid rgba(131, 167, 203, 0.85);
  background: rgba(255, 255, 255, 0.88);
  box-shadow: 0px 6px 16px rgba(0, 0, 0, 0.06);
  padding: 14px;
  margin-bottom: 14px;
}

.card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 10px;
}
.card-title {
  font-weight: 900;
  font-size: 16px;
}

.micro {
  font-size: 12px;
  font-weight: 700;
}
.subtle { opacity: 0.8; }
.dim { opacity: 0.7; }

/* Filters */
.filters {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 10px;
  align-items: end;
  margin-bottom: 12px;
}

.field .k {
  display: block;
  font-size: 12px;
  font-weight: 900;
  opacity: 0.7;
  margin-bottom: 6px;
}

.input {
  width: 100%;
  padding: 12px 12px;
  border-radius: 14px;
  border: 1px solid rgba(176, 196, 222, 0.95);
  background: #f6fbff;
  font-weight: 800;
  font-size: 14px;
}

.btns {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  flex-wrap: wrap;
  align-items: center;
}

/* Snapshot cards */
.snapshot {
  display: grid;
  grid-template-columns: repeat(4, minmax(140px, 1fr));
  gap: 10px;
}
.snap {
  background: #f6fbff;
  border: 1px solid rgba(176, 196, 222, 0.75);
  border-radius: 14px;
  padding: 10px;
}
.snap .k { font-weight: 900; font-size: 12px; opacity: 0.7; }
.snap .v { font-weight: 900; font-size: 14px; margin-top: 4px; }

/* Table */
.table-wrap { overflow: auto; }
.table {
  width: 100%;
  border-collapse: collapse;
  min-width: 860px;
}
.table th, .table td {
  border: 1px solid rgba(131, 167, 203, 0.55);
  padding: 10px 8px;
  background: rgba(255, 255, 255, 0.65);
}
.table thead th {
  position: sticky;
  top: 0;
  background: #eef4fb;
  z-index: 3;
}
.left { text-align: left; }
.right { text-align: right; }

.row-title {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.section td {
  background: rgba(238, 244, 251, 0.85);
}
.total td {
  background: rgba(246, 251, 255, 0.92);
}

.pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 900;
  font-size: 12px;
  border: 1px solid rgba(176, 196, 222, 0.75);
  background: rgba(246, 251, 255, 0.9);
}
.pill.hot  { border-color: rgba(255, 140, 0, 0.45); }
.pill.good { border-color: rgba(40, 134, 113, 0.45); }
.pill.thin { border-color: rgba(79, 103, 115, 0.55); }
.pill.meh  { border-color: rgba(131, 167, 203, 0.75); }
.pill.loss { border-color: rgba(180, 60, 60, 0.45); }
.pill.neutral { opacity: 0.75; }

.mini {
  border: 1px solid rgba(79, 103, 115, 0.25);
  background: #4f6773;
  color: white;
  font-weight: 900;
  padding: 8px 10px;
  border-radius: 999px;
  cursor: pointer;
}
.mini:hover { background: #3a4a5e; }
.mini.ghost { background: #4f6773; }

.mono {
  font-variant-numeric: tabular-nums;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}
.strong { font-weight: 900; }
.neg { color: #b43c3c; font-weight: 900; }

/* Footer */
.foot {
  margin-top: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

/* Empty */
.empty { text-align: center; }
.empty-title { font-weight: 900; font-size: 18px; }
.empty-sub { margin-top: 6px; opacity: 0.8; font-weight: 800; }

.toast {
  position: fixed;
  top: 14px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  padding: 10px 12px;
  border-radius: 999px;
  font-weight: 900;
  border: 1px solid rgba(176, 196, 222, 0.95);
  background: rgba(246, 251, 255, 0.92);
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.18);
}

.skeleton-table {
  display: grid;
  gap: 10px;
}
.sk-row {
  display: grid;
  grid-template-columns: 1.2fr 0.6fr;
  gap: 10px;
}
.sk-bar {
  height: 12px;
  border-radius: 999px;
  background: rgba(176, 196, 222, 0.55);
  position: relative;
  overflow: hidden;
}
.sk-bar::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(
    90deg,
    rgba(255,255,255,0) 0%,
    rgba(255,255,255,0.6) 50%,
    rgba(255,255,255,0) 100%
  );
  animation: shimmer 1.2s infinite;
}
@keyframes shimmer { 100% { transform: translateX(100%); } }
.w55 { width: 55%; }
.w20 { width: 20%; }

@media (max-width: 980px) {
  .filters { grid-template-columns: 1fr; }
  .snapshot { grid-template-columns: 1fr; }
  .table { min-width: 720px; }
}
</style>
